import { NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { environment } from '@fantaz/shared/app-config';
import { GAEventKeys } from '@fantaz/shared/data-access/models';
import { Store } from '@ngrx/store';
import { HotToastRef } from '@ngxpert/hot-toast';
import { HeadlessService, IMessage } from '@novu/headless';
import { NotificationCenterModule as NotificationCenterModuleNovu } from '@novu/notification-center-angular';
import { TrackError } from 'libs/core/src/lib/utils/track-error.service';
import { SnackbarService } from 'libs/core/src/lib/utils/snackbar.service';
import { FzZendeskService } from 'libs/shared/data-access/api/src/lib/services/fz-zenddesk.service';
import { darkTheme, lightTheme } from './themes';

@Component({
  selector: 'fz-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NotificationCenterModuleNovu, RouterModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class NotificationCenterComponent implements OnInit {
  /*
   * Required services
   */
  #snackbarService = inject(SnackbarService);
  #fzZendeskService = inject(FzZendeskService);

  /**
   * User data
   */
  @Input() subscriberId!: string;

  /**
   * Theme for the notification center, either 'dark' or 'light'.
   * When theme is set, the color scheme is switched to the corresponding theme.
   */
  @Input() set theme(value: string | undefined) {
    if (value) {
      this.switchColorScheme(value);
    }
  }

  constructor(private store: Store) {}

  isMobile = window.innerWidth < 599;
  styles = signal(darkTheme(this.isMobile));

  title = 'novu-angular-notification-center';

  applicationIdentifier = environment.notifications.NovuApplicationIdentifier;

  headlessService: HeadlessService;

  @ViewChild('snackbarTemplate') notificationTmp: any;

  // optional props for notification center
  backendUrl = 'https://api.novu.co';
  socketUrl = 'https://ws.novu.co';
  subscriberHash = 'SUBSCRIBER_HASH';
  showUserPreferences = false;
  allowedNotificationActions = true;
  popoverConfig = { offset: 10 };
  i18n = {
    lang: 'en',

    translations: {
      poweredBy: 'Powered By',
    },
  };

  ngOnInit(): void {
    /*
     * Create  a new session with the notification service
     */
    this.headlessService = new HeadlessService({
      applicationIdentifier:
        environment.notifications.NovuApplicationIdentifier,
      subscriberId: this.subscriberId,
    });

    /*
     * Initialize the session with the notification service
     */
    this.initializeHeadlessService();

    this.interceptXHR();
  }

  /**
   * Intercepts XMLHttpRequests and logs or blocks requests based on the URL.
   */
  interceptXHR() {
    const XHR = XMLHttpRequest.prototype;
    const open = XHR.open;

    XHR.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
    ) {
      // Check if the URL does not contain both 'api.novu.co' and 'primary'
      if (
        !(
          url.toString().includes('api.novu.co') &&
          url.toString().includes('primary')
        )
      ) {
        return open.apply(this, arguments as any);
      }
    };
  }

  /**
   * Initializes the headless service.
   */
  private initializeHeadlessService() {
    this.headlessService.initializeSession({
      listener: () => {},
      onSuccess: () => {
        this.listenForNotifications();
      },
      onError: (error: any) => {
        TrackError.trackCustomError(
          'Novu headless session failure',
          'notification-center',
        );
      },
    });
  }

  /**
   * Listens for notifications and handles the received messages.
   */
  private listenForNotifications() {
    this.headlessService.listenNotificationReceive({
      listener: (message: IMessage) => {
        this.onNotificationReceived(message);
      },
    });
  }

  /**
   * Handles the received notification.
   */
  onNotificationReceived(notification: IMessage) {
    this.animateBellIcon();
    this.showToast(notification);
  }

  showToast(notification: IMessage) {
    const { content, cta, _id } = notification;
    const ctaText = cta?.action?.buttons?.[0]?.content || null;
    this.#snackbarService.showSnackbar(NotificationToastComponent, {
      position: 'top-center',
      duration: 5000,
      style: {
        'border-radius': '12px',
        border: '1px solid #FF9B58',
        padding: '12px 16px ',
        'margin-top': '88px',
      },
      data: {
        content,
        cta: ctaText,
        _id,
        onClick: () => {
          /*
           * Mark the notification as read
           */
          this.actionClicked(_id, '', notification);
        },
      },
    });
  }

  makePromise(
    serviceCall: (callbacks: {
      onSuccess: () => void;
      onError: (error: any) => void;
    }) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      serviceCall({
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  }

  async clickedOnCTA(message: IMessage, route: string) {
    try {
      await Promise.all([
        this.makePromise((callbacks) =>
          this.headlessService.markAllMessagesAsSeen({
            listener: () => {},
            onSuccess: callbacks.onSuccess,
            onError: (error: any) => {
              TrackError.trackCustomError(
                'Failure marking all messages as seen',
                'notification-center',
              );
              callbacks.onError(error);
            },
          }),
        ),
        this.makePromise((callbacks) =>
          this.headlessService.markNotificationsAsRead({
            messageId: message._id,
            listener: () => {},
            onSuccess: callbacks.onSuccess,
            onError: (error: any) => {
              TrackError.trackCustomError(
                'Failure marking notifications as read',
                'notification-center',
              );
              callbacks.onError(error);
            },
          }),
        ),
      ]);
      if (!message.payload['support']) window.location.href = route;
      else {
        this.#fzZendeskService.openZendesk();
      }
    } catch (error) {
      console.error('An error occurred during the operation:', error);
    }
  }

  animateBellIcon() {
    const svgElements = document.querySelectorAll('.nc-bell-button>svg');
    const classNotificationReceived = 'notification-received';
    svgElements.forEach((element) => {
      element.classList.add(classNotificationReceived);
    });

    setTimeout(() => {
      svgElements.forEach((element) => {
        element.classList.remove(classNotificationReceived);
      });
    }, 3000);
  }

  sessionLoaded = (data: unknown) => {
    console.log('loaded', { data });
  };

  notificationClicked = (message: any) => {
    console.log('Notification clicked:', message);
  };

  unseenCountChanged = (count: number) => {};

  /**
   * Handles the click event of an action button in the notification center component.
   * @param workflowTriggerIdentifier - The identifier of the workflow trigger.
   * @param actionButtonType - The type of the action button.
   * @param message - The notification message.
   */
  actionClicked = (
    workflowTriggerIdentifier: string,
    actionButtonType: string,
    message: any,
  ) => {
    console.log(
      'Action clicked - workflowTriggerIdentifier:',
      message,
      workflowTriggerIdentifier,
      actionButtonType,
    );
    const urlDecoded = this.decodeHtmlEntity(message.cta.data.url);
    const route = urlDecoded;
    this.store.dispatch(
      CoreGoogleAnalyticActions.trackEvent({
        event: {
          event: GAEventKeys.CUSTOM_CLICK,
          ga_value: 'in-app-notification-action-click',
          click_url: route,
          message_id: message._id,
        },
      }),
    );

    this.clickedOnCTA(message, route);
  };

  decodeHtmlEntity(text: string): string {
    const doc = document.createElement('textarea');
    doc.innerHTML = text;
    return doc.value;
  }

  switchColorScheme(theme: string): void {
    if (theme === 'dark') {
      this.styles.set(darkTheme(this.isMobile));
    }

    if (theme === 'light') {
      this.styles.set(lightTheme(this.isMobile));
    }
  }
}

type DataType = {
  content: string;
  cta: string;
  onClick: () => void;
};

@Component({
  selector: 'fz-notification-toast',
  standalone: true,
  template: `
    <div class="toast-content">
      <div class="text-and-close-icon">
        <p [innerHTML]="toastRef.data.content" class="mat-body-1"></p>
        <mat-icon (click)="closeToast()">close</mat-icon>
      </div>
      <button
        color="primary"
        class="mat-body-1"
        (click)="toastRef.data.onClick()"
        *ngIf="toastRef?.data?.cta"
      >
        {{ toastRef.data.cta }}
      </button>
    </div>
  `,
  imports: [MatButtonModule, MatIconModule, NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./notification-center.component.scss'],
})
export class NotificationToastComponent {
  constructor(
    @Optional() @Inject(HotToastRef) public toastRef: HotToastRef<DataType>,
  ) {}

  closeToast() {
    this.toastRef.close();
  }
}
